/**
 * BeastLife — GA4 click events (direct gtag, no GTM)
 * ---------------------------------------------------------------------------
 * File:    assets/bl-ga4-events.js
 * Install: layout/theme.liquid, just before </body>:
 *
 *   <script src="{{ 'bl-ga4-events.js' | asset_url }}" defer="defer"></script>
 *
 * Events:  section_click, section_button_click, footer_click,
 *          category_click, product_card_click
 *          (banner_click is NOT here — it's already live via GTM.)
 *
 * Transport: gtag('event', …, { send_to: 'G-70EHFXV66V' })
 *   theme.liquid already loads gtag.js for G-70EHFXV66V in <head> (GoKwik block).
 *   NOTE: dataLayer.push({ event: 'x' }) on its own is a GTM convention and is
 *   ignored by gtag.js — that's why events go through gtag() here.
 *
 * Built against the live DOM (Dawn-based theme, Sept 2026):
 *   sections      #MainContent .shopify-section[id^="shopify-section-template--"]
 *   product cards .card-wrapper.product-card-wrapper  (a#CardLink-<section>-<productId>)
 *   categories    a.bl-cat-item, a.sbf__card, .menuitmes a, .singleitem a
 *   footer        footer.ft  (.ft__nav-col > .ft__nav-title, a.ft__nav-link)
 *
 * Optional theme upgrade (snippets/card-product.liquid, on .card-wrapper):
 *   data-product-id="{{ card_product.id }}"
 *   data-sku="{{ card_product.selected_or_first_available_variant.sku }}"
 *   data-product-type="{{ card_product.type }}"
 *   → item_id becomes SKU and item_category gets populated. Without it,
 *     item_id = Shopify product ID and item_category is omitted.
 *
 * Debug: add ?bl_debug=1 to any URL → console logs + GA4 DebugView.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__blGa4Bound) return;
  window.__blGa4Bound = true;
  if (window.Shopify && window.Shopify.designMode) return; // no tracking inside the theme editor

  /* ======================================================================
   * CONFIG
   * ==================================================================== */
  var CONFIG = {
    measurementId: 'G-70EHFXV66V',
    brand: 'BeastLife',

    // section_click / section_button_click only on the homepage (per spec).
    // false → run on every page.
    sectionEventsHomeOnly: true,

    // 'always'   → section_click fires on EVERY tracked click inside a section,
    //              even when a more specific event (button/product/category)
    //              also fires. Clean per-section totals, 2 events per click.
    // 'fallback' → section_click only when no specific event matched. 1 event per click.
    sectionClickMode: 'always',

    // true  → only clicks on interactive elements (links, buttons, tabs, summaries…)
    //         count as section_click.
    // false → literally any click in the section (spec wording — noisy, dead clicks on text).
    sectionClickInteractiveOnly: true,

    // GA4 caps every param value at 100 chars. Same-origin paths stay under it;
    // absolute URLs for long product handles do not.
    // 'path'     → /collections/shop            (external links stay absolute)
    // 'absolute' → https://beastlife.in/collections/shop
    urlMode: 'path',

    // true → also mirror each event as dataLayer.push({ event, …params })
    //        so GTM triggers can pick them up later if you ever want them there.
    mirrorToDataLayer: false,

    // Force console logging + debug_mode (DebugView). ?bl_debug=1 also enables it.
    debug: false
  };

  // Friendly section names, keyed by section type (the id minus its random suffix).
  // Section headings on this theme are split across elements ("Quality That" / "Stands Apart"),
  // so a map beats scraping them. Unknown sections fall back to heading → type.
  var SECTION_NAMES = {
    cro_custom_slider:     'Hero Banner',
    ticker_bar:            'USP Ticker',
    category_icon_row:     'Category Icons',
    featured_tabs:         'Bestsellers',
    protein_goes_to_waste: 'Protein Goes To Waste (Ultrasorb)',
    shop_by_flavors:       'Shop By Flavors',
    why_stands_apart:      'Why BeastLife Stands Apart',
    sustain_technology:    'Everyday Wellness',
    featured_collection:   'Daily Essentials',
    brand_promise:         '10 Lakh Guarantee + Annamrita',
    what_customers_think:  'Customer Reviews',
    quality_stands_apart:  'Quality Certifications',
    why_beastlife:         'Comparison + Featured & Trusted',
    section_heading:       'Learning & Flexing (heading)',
    '17151677426a79bc8f':  'Learning & Flexing (content)',
    rich_text:             'Rich Text',
    faq_section:           'FAQ'
  };

  var DEBUG = CONFIG.debug || /[?&]bl_debug=1/.test(location.search);
  var IS_HOME = /^\/(?:[a-z]{2}(?:-[a-z]{2})?)?\/?$/i.test(location.pathname);

  /* ======================================================================
   * SELECTORS (all site-specific knowledge lives here)
   * ==================================================================== */
  var SEL = {
    section: '.shopify-section',
    templateSections: '#MainContent .shopify-section[id^="shopify-section-template--"]',
    sectionHeading: 'h1, h2, .title, .sectionheading, .section-heading',

    // Product cards (Dawn) — any page
    productCard: '.product-card-wrapper, .card-wrapper, [data-product-card]',
    productCardExclude: '.quick-add, .quick-add__submit, product-form, form[action*="/cart/add"], [name="add"], .shopify-payment-button, [class*="buy-now"], [class*="wishlist"]',
    cardTitleLink: '.card__heading a, a[id^="CardLink-"]',
    cardListItem: 'li.grid__item, .grid__item, .swiper-slide, .slick-slide, li',
    tabPanel: '.fc-tab-panel',
    activeTab: '.fc-tab-btn[aria-selected="true"]',
    priceSale: '.price__sale .price-item--sale',
    priceRegular: '.price__regular .price-item--regular, .price-item--regular',

    // Category tiles / links
    category: [
      'a.bl-cat-item',                                                 // homepage category icon row
      'a.sbf__card',                                                   // homepage shop-by-flavors
      '.menuitmes a',                                                  // desktop mega menu tiles
      '.custom-mobile-drawer .singleitem a',                           // mobile drawer tiles
      '.shopify-section-group-header-group a[href*="/collections/"]'  // any other header collection link
    ].join(', '),

    // Footer
    footer: 'footer.ft, .shopify-section-group-footer-group, footer',
    footerColumn: '.ft__nav-col',
    footerColumnTitle: '.ft__nav-title',
    footerToggle: '.ft__nav-toggle',

    // Buttons inside sections
    button: 'button, .button, [role="button"], [role="tab"], input[type="submit"], a[class*="cta"], a[class*="btn"]',
    buttonExclude: '.quick-add__submit, form[action*="/cart/add"] button, [name="add"], .shopify-payment-button',

    // What counts as "interactive" for section_click
    interactive: 'a, button, [role="button"], [role="tab"], summary, input, select, textarea, label, video, [onclick]',

    banner: '.gtm-banner-click, .homepage-slider'
  };

  /* ======================================================================
   * TRANSPORT
   * ==================================================================== */
  function ensureGtag() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      // Standard shim: queues into dataLayer; gtag.js (already in <head>) consumes it.
      window.gtag = function () { window.dataLayer.push(arguments); };
      if (DEBUG) console.warn('[BL GA4] window.gtag was missing — installed shim. Check the gtag block in theme.liquid.');
    }
  }

  function send(name, params) {
    var clean = {};
    for (var k in params) {
      if (!Object.prototype.hasOwnProperty.call(params, k)) continue;
      var v = params[k];
      if (v === undefined || v === null || v === '') continue;
      clean[k] = v;
    }

    var payload = {};
    for (var p in clean) payload[p] = clean[p];
    payload.send_to = CONFIG.measurementId;
    payload.transport_type = 'beacon';
    if (DEBUG) payload.debug_mode = true;

    ensureGtag();
    try { window.gtag('event', name, payload); } catch (e) { if (DEBUG) console.error('[BL GA4]', e); }

    if (CONFIG.mirrorToDataLayer) {
      var dl = { event: name };
      for (var m in clean) dl[m] = clean[m];
      window.dataLayer.push(dl);
    }
    if (DEBUG) console.log('[BL GA4] ' + name, clean);
  }

  /* ======================================================================
   * HELPERS
   * ==================================================================== */
  function txt(el, max) {
    if (!el) return '';
    var t = (el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim();
    return t.slice(0, max || 100);
  }

  function altOf(el) {
    if (!el) return '';
    var img = el.tagName === 'IMG' ? el : el.querySelector('img[alt]');
    return img ? (img.getAttribute('alt') || '').trim().slice(0, 100) : '';
  }

  function label(el, max) {
    if (!el) return '';
    var n = max || 100;
    return txt(el, n) || (el.getAttribute('aria-label') || '').slice(0, n) ||
           (el.getAttribute('title') || '').slice(0, n) || altOf(el);
  }

  function url(href) {
    if (!href || /^(javascript:|#|mailto:|tel:)/i.test(href)) return '';
    try {
      var u = new URL(href, location.href);
      var out = (CONFIG.urlMode === 'path' && u.origin === location.origin)
        ? u.pathname + u.search
        : u.href;
      return out.slice(0, 100);
    } catch (e) { return String(href).slice(0, 100); }
  }

  function hostLabel(href) {
    try { return new URL(href, location.href).hostname.replace(/^www\./, '').split('.')[0]; }
    catch (e) { return ''; }
  }

  function money(s) {
    var n = parseFloat(String(s || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? undefined : n;
  }

  function indexIn(list, el) {
    var i = Array.prototype.indexOf.call(list, el);
    return i < 0 ? undefined : i + 1;
  }

  function collectionHandle() {
    return (location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || '';
  }

  function isTemplateSection(section) {
    return !!section && /^shopify-section-template--/.test(section.id || '') &&
           !section.closest('header, footer');
  }

  // "shopify-section-template--123__featured_tabs_nycRhK"
  //   → { id: 'featured_tabs_nycRhK', type: 'featured_tabs', name: 'Bestsellers', position: 4 }
  var sectionCache = {};
  function sectionInfo(section) {
    var raw = (section.id || '').replace(/^shopify-section-/, '');
    if (sectionCache[raw]) return sectionCache[raw];
    var id = raw.indexOf('__') > -1 ? raw.split('__').pop() : raw;
    var type = id.replace(/_[A-Za-z0-9]{6}$/, '');
    var coll = collectionHandle();
    var name = SECTION_NAMES[type] || SECTION_NAMES[id] ||
               txt(section.querySelector(SEL.sectionHeading), 60) ||
               (coll ? 'collection:' + coll : type);
    var position = indexIn(document.querySelectorAll(SEL.templateSections), section);
    return (sectionCache[raw] = { id: id, type: type, name: name, position: position });
  }

  /* ======================================================================
   * EVENT BUILDERS
   * ==================================================================== */
  function trackFooter(target) {
    var el = target.closest('a, button, [role="button"], summary');
    if (!el) return;
    var col = el.closest(SEL.footerColumn);
    var column = col ? txt(col.querySelector(SEL.footerColumnTitle), 40)
      : el.closest('.ft__newsletter') ? 'newsletter'
      : el.closest('.ft__brand')      ? 'brand'
      : el.closest('.ft__copyright')  ? 'copyright'
      : el.closest('.ft__payment')    ? 'payment'
      : 'footer';
    var href = el.getAttribute('href') || '';
    var text = el.matches(SEL.footerToggle)
      ? 'toggle:' + column
      : (label(el) || hostLabel(href) || el.tagName.toLowerCase());

    send('footer_click', {
      link_text: text,
      footer_column: column || 'footer',
      destination_url: url(href)
    });
  }

  function categorySource(a) {
    if (a.closest('.bl-cat-section, .bl-cat'))            return 'homepage_category_grid';
    if (a.closest('.section-shop-by-flavors, .sbf'))      return 'homepage_shop_by_flavors';
    if (a.closest('.megamenudropdown'))                    return 'header_mega_menu';
    if (a.closest('.menu-drawer, .custom-mobile-drawer'))  return 'mobile_menu';
    if (a.closest('.shopify-section-group-header-group, header')) return 'header_nav';
    var s = a.closest(SEL.section);
    return s ? sectionInfo(s).name : location.pathname;
  }

  function trackCategory(a) {
    var href = a.getAttribute('href') || '';
    if (/^(javascript:|#)/i.test(href)) return; // "Products" parent toggle etc.
    var handle = (href.match(/\/collections\/([^/?#]+)/) || [])[1];
    var pathId = '';
    try { pathId = new URL(href, location.href).pathname.replace(/^\/+|\/+$/g, ''); } catch (e) {}

    send('category_click', {
      category_id: handle || pathId,          // collection handle (no collection ID in the DOM)
      category_name: label(a, 60),
      source_location: categorySource(a),
      destination_url: url(href)
    });
  }

  function trackProductCard(card, target) {
    var titleLink = card.querySelector(SEL.cardTitleLink) || card.querySelector('a[href*="/products/"]');
    var clicked = target.closest('a[href]');
    var href = (clicked && clicked.getAttribute('href')) || (titleLink && titleLink.getAttribute('href')) || '';
    if (!/\/products\//.test(href)) return; // not a product card after all

    var handle = (href.match(/\/products\/([^/?#]+)/) || [])[1] || '';
    var productId = card.getAttribute('data-product-id') ||
                    (titleLink && titleLink.id && (titleLink.id.match(/-(\d+)$/) || [])[1]) || '';
    var sku = card.getAttribute('data-sku') || '';
    var type = card.getAttribute('data-product-type') || '';
    var name = txt(titleLink, 100) || altOf(card) || handle;
    var price = money(txt(card.querySelector(SEL.priceSale))) || money(txt(card.querySelector(SEL.priceRegular)));

    var li = card.closest(SEL.cardListItem);
    var position = li && li.parentElement ? indexIn(li.parentElement.children, li) : undefined;

    var section = card.closest(SEL.section);
    var source;
    if (section && isTemplateSection(section)) {
      source = sectionInfo(section).name;
      if (card.closest(SEL.tabPanel)) {                       // "Bestsellers / Creatine"
        var tab = section.querySelector(SEL.activeTab);
        if (tab) source += ' / ' + txt(tab, 30);
      }
    } else {
      source = collectionHandle() ? 'collection:' + collectionHandle() : location.pathname;
    }

    var itemId = sku || productId || handle;

    send('product_card_click', {
      item_id: itemId,
      item_name: name,
      item_brand: CONFIG.brand,
      item_category: type,
      price: price,
      card_position: position,
      source_section: source,
      destination_url: url(href),
      // GA4-native items[] → item-scoped dimensions (Item name/brand/category/list name)
      // populate with zero admin setup, unlike the flat params above.
      items: [{
        item_id: itemId,
        item_name: name,
        item_brand: CONFIG.brand,
        item_category: type || undefined,
        price: price,
        index: position,
        item_list_name: source
      }]
    });
  }

  function trackSectionButton(btn, section) {
    var s = sectionInfo(section);
    send('section_button_click', {
      section_id: s.id,
      section_name: s.name,
      button_text: label(btn, 80) || btn.value || 'unlabeled',
      destination_url: url(btn.getAttribute('href') || '')
    });
  }

  function clickType(target, ctx) {
    if (target.closest(SEL.banner))         return 'banner';
    if (ctx.cardExcluded)                   return 'add_to_cart';
    if (ctx.card)                           return 'product_card';
    if (ctx.cat)                            return 'category';
    if (target.closest('[role="tab"]'))     return 'tab';
    if (ctx.btn)                            return 'button';
    if (target.closest('summary, details')) return 'accordion';
    if (target.closest('a'))                return 'link';
    if (target.closest('video'))            return 'video';
    return 'other';
  }

  function trackSection(target, section, ctx) {
    var s = sectionInfo(section);
    var el = target.closest(SEL.interactive) || target;
    send('section_click', {
      section_id: s.id,
      section_name: s.name,
      section_position: s.position,
      click_type: clickType(target, ctx),
      element_text: label(el, 60) || el.tagName.toLowerCase()
    });
  }

  /* ======================================================================
   * ROUTER — one delegated listener, capture phase so slider / quick-add
   * handlers that stopPropagation() can't swallow it.
   * ==================================================================== */
  function onClick(e) {
    if (e.type === 'auxclick' && e.button !== 1) return; // auxclick: middle-click only
    var target = e.target && e.target.nodeType === 3 ? e.target.parentElement : e.target;
    if (!target || !target.closest) return;

    // 1. Footer — own event, nothing else fires
    if (target.closest(SEL.footer)) { trackFooter(target); return; }

    var fired = false;

    // 2. Category tiles / nav links (any page)
    var cat = target.closest(SEL.category);
    if (cat) { trackCategory(cat); fired = true; }

    // 3. Product cards (any page), excluding Add to Cart / Buy Now / wishlist
    var card = cat ? null : target.closest(SEL.productCard);
    var cardExcluded = !!(card && target.closest(SEL.productCardExclude));
    if (card && !cardExcluded) { trackProductCard(card, target); fired = true; }

    // 4. Section-scoped events (homepage by default)
    var section = target.closest(SEL.section);
    if (!isTemplateSection(section)) return;
    if (CONFIG.sectionEventsHomeOnly && !IS_HOME) return;

    var btn = (cat || card) ? null : target.closest(SEL.button);
    if (btn && section.contains(btn) && !btn.closest(SEL.buttonExclude)) {
      trackSectionButton(btn, section);
      fired = true;
    }

    if (CONFIG.sectionClickMode === 'always' || !fired) {
      if (!CONFIG.sectionClickInteractiveOnly || target.closest(SEL.interactive)) {
        trackSection(target, section, { cat: cat, card: card, cardExcluded: cardExcluded, btn: btn });
      }
    }
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);

  // Console access for QA: BL_GA4.send('test_event', { foo: 'bar' }); BL_GA4.config.debug = true
  window.BL_GA4 = { version: '1.0.0', config: CONFIG, sectionNames: SECTION_NAMES, selectors: SEL, send: send };
  if (DEBUG) console.log('[BL GA4] ready', { home: IS_HOME, id: CONFIG.measurementId });
})();